/**
 * Linked Providers — SecureLink Discharge Import patient surface.
 * Not ChartMind-I/I2 inpatient med-rec, not CareStart Plus discharge.
 * Lists `ehrLinks` for the signed-in patient; Sync/Revoke/Renew call
 * Cloud Functions. No live EHR vendor from this page — json-schema links
 * await a push; FHIR sync is still mock/scaffold credentials.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { format, formatDistanceToNow } from "date-fns";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { contentCardSx } from "styles/standaloneLayoutTokens";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";
import {
  renewEhrLinkConsent,
  revokeEhrLink,
  syncFromEhrLink,
} from "services/cmr/ehrDischargeService";
import {
  isEhrLinkConsentDue,
  toConsentDate,
} from "services/cmr/ehrLinkConsentWindow";

function toJsDate(value) {
  return toConsentDate(value);
}

function ialLabel(level) {
  if (level === "staff_in_person") return "Verified in person by staff";
  const n = Number(level);
  if (n === 1) return "Self-reported identity";
  if (n === 2) return "ID document verified";
  if (n === 3) return "Verified in person";
  return null;
}

function linkedLabel(linkedAt) {
  const d = toJsDate(linkedAt);
  if (!d) return "Linked date unavailable";
  return `Linked ${format(d, "MMMM d, yyyy")}`;
}

function lastSyncedLabel(lastSyncedAt) {
  const d = toJsDate(lastSyncedAt);
  if (!d) return "Not yet synced";
  const ageMs = Date.now() - d.getTime();
  if (ageMs >= 0 && ageMs < 60 * 1000) return "Last synced Just now";
  return `Last synced ${formatDistanceToNow(d, { addSuffix: true })}`;
}

const EhrLinksPage = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [pendingByLink, setPendingByLink] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [listenTick, setListenTick] = useState(0);
  const [syncingId, setSyncingId] = useState(null);
  const [renewingId, setRenewingId] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncNotice, setSyncNotice] = useState(null);

  const reload = useCallback(() => {
    setListenTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    setLoadError(false);
    const unsub = db
      .collection("ehrLinks")
      .doc(user.uid)
      .collection("links")
      .where("status", "==", "active")
      .onSnapshot(
        (snap) => {
          const rows = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          setLinks(rows);
          setLoadError(false);
          setLoading(false);
        },
        () => {
          setLoadError(true);
          setLinks([]);
          setLoading(false);
        },
      );
    return () => unsub();
  }, [user?.uid, listenTick]);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsub = db
      .collection("pendingMedReconciliations")
      .doc(user.uid)
      .collection("items")
      .onSnapshot(
        (snap) => {
          const map = {};
          snap.forEach((doc) => {
            const data = doc.data() || {};
            if (data.status !== "completed") {
              map[doc.id] = data;
            }
          });
          setPendingByLink(map);
        },
        () => setPendingByLink({}),
      );
    return () => unsub();
  }, [user?.uid]);

  const handleSync = useCallback(async (linkId) => {
    setSyncError(null);
    setSyncNotice(null);
    setSyncingId(linkId);
    try {
      const result = await syncFromEhrLink({ linkId });
      if (result?.awaitingJson) {
        setSyncNotice({ linkId, awaitingJson: true });
        return;
      }
      if (!result?.success) {
        setSyncError("Sync failed — try again later.");
        return;
      }
      setSyncNotice({ linkId, justNow: true });
    } catch {
      setSyncError("Sync failed — try again later.");
    } finally {
      setSyncingId(null);
    }
  }, []);

  const handleRenew = useCallback(async (linkId) => {
    setRenewingId(linkId);
    try {
      await renewEhrLinkConsent({ linkId });
    } catch {
      setSyncError("Could not renew access. Please try again.");
    } finally {
      setRenewingId(null);
    }
  }, []);

  const confirmRevoke = useCallback(async () => {
    if (!revokeTarget) return;
    setRevokeLoading(true);
    try {
      await revokeEhrLink({ linkId: revokeTarget.id });
      setRevokeTarget(null);
    } catch {
      setSyncError("Could not remove access. Please try again.");
    } finally {
      setRevokeLoading(false);
    }
  }, [revokeTarget]);

  const empty = !loading && !loadError && links.length === 0;

  return (
    <Box sx={{ mb: 4 }}>
      <DashboardPageHeader
        title="Linked Providers"
        subtitle="Healthcare institutions connected to your MasterChart"
        sx={{ mt: { xs: 1, sm: 5 } }}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {loadError ? (
        <DataLoadErrorAlert
          message="Unable to load linked providers."
          onRetry={reload}
        />
      ) : null}

      {syncError ? (
        <Alert severity="error" onClose={() => setSyncError(null)} sx={{ mb: 2 }}>
          {syncError}
        </Alert>
      ) : null}

      {empty ? (
        <Typography color="text.secondary">
          No linked healthcare providers yet. When you scan a SecureLink QR from
          discharge instructions, your records will appear here.
        </Typography>
      ) : null}

      <Stack spacing={2}>
        {links.map((link) => {
          const pending = pendingByLink[link.id];
          const showJustNow =
            syncNotice?.linkId === link.id && syncNotice.justNow;
          const showAwaitingJson =
            syncNotice?.linkId === link.id && syncNotice.awaitingJson;
          return (
            <Paper key={link.id} sx={{ ...contentCardSx, p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Typography variant="h6">
                  {link.institutionName || "Healthcare provider"}
                </Typography>
                {ialLabel(link.identityAssuranceLevel) ? (
                  <Chip
                    size="small"
                    label={ialLabel(link.identityAssuranceLevel)}
                  />
                ) : null}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {linkedLabel(link.linkedAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {showJustNow
                  ? "Last synced Just now"
                  : showAwaitingJson
                    ? "This facility pushes records. Sync does not pull — they appear when the hospital sends them."
                    : lastSyncedLabel(link.lastSyncedAt)}
              </Typography>

              {pending ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Medication review pending{" "}
                  <RouterLink to={`/dashboard/med-reconciliation/${link.id}`}>
                    Review medications
                  </RouterLink>
                </Alert>
              ) : null}

              {syncNotice?.linkId === link.id && pending ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Medication changes ready to review{" "}
                  <RouterLink to={`/dashboard/med-reconciliation/${link.id}`}>
                    Review medications
                  </RouterLink>
                </Alert>
              ) : null}

              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                <LoadingButton
                  onClick={() => handleSync(link.id)}
                  loading={syncingId === link.id}
                >
                  Sync now
                </LoadingButton>
                {isEhrLinkConsentDue(link.nextConsentPromptAt) ? (
                  <LoadingButton
                    onClick={() => handleRenew(link.id)}
                    loading={renewingId === link.id}
                  >
                    Keep access
                  </LoadingButton>
                ) : null}
                <LoadingButton
                  color="error"
                  onClick={() => setRevokeTarget(link)}
                >
                  Remove access
                </LoadingButton>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title={`Remove ${revokeTarget?.institutionName || "this provider"}?`}
        message="This stops FusionMSI from receiving future records from this institution. Records already in your MasterChart are not deleted."
        confirmLabel="Remove"
        confirmColor="error"
        loading={revokeLoading}
        onConfirm={confirmRevoke}
        onClose={() => setRevokeTarget(null)}
      />
    </Box>
  );
};

export default EhrLinksPage;
